import { cn } from '@/core/string.core';
import { LifecycleStatus, TemplatePublicationStatus } from '@/gql/graphql';
import { VariantProps, cva } from 'class-variance-authority';
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover.component';

const pillVariants = cva(
  cn(
    'typo-primary-300 bg-system-success-bg text-system-success px-3 rounded-[20px] inline-flex line-clamp-1 inline-block whitespace-nowrap overflow-hidden',
  ),
  {
    variants: {
      variant: {
        default: '',
        neutral: 'bg-gray-240 text-secondary-text',
        dark: 'bg-dark text-white',
        db: 'bg-pill-db-bg text-pill-db',
        bb: 'bg-pill-bb-bg text-pill-bb',
        ms: 'bg-pill-ms-bg text-pill-ms',
        do: 'bg-pill-do-bg text-pill-do',
        bu: 'bg-light-gray text-black',
        warning: 'bg-system-warning-bg text-system-warning',
      },
      size: {
        default: 'typo-primary-500',
        medium: 'text-xs py-1',
        small: 'typo-label rounded-[4px] px-2 py-1',
      },
      uppercase: {
        true: 'uppercase',
        false: '',
      },
      productStatus: {
        [LifecycleStatus.New]:
          'h-[20px] flex items-center rounded-[2px] px-1 justify-center typo-label bg-product-status-new text-product-status-new-text',
        [LifecycleStatus.InProgress]:
          'h-[20px] flex items-center rounded-[2px] px-1 justify-center typo-label bg-product-status-in-progress text-product-status-in-progress-text',
        [LifecycleStatus.Retired]:
          'h-[20px] flex items-center rounded-[2px] px-1 justify-center typo-label bg-product-status-retired text-product-status-retired-text',
        [LifecycleStatus.Transmitted]:
          'h-[20px] flex items-center rounded-[2px] px-1 justify-center typo-label bg-product-status-transmitted text-product-status-transmitted-text',
        [LifecycleStatus.Completed]:
          'h-[20px] flex items-center rounded-[2px] px-1 justify-center typo-label bg-product-status-transmitted text-product-status-transmitted-text',
        [LifecycleStatus.Outdated]:
          'h-[20px] flex items-center rounded-[2px] px-1 justify-center typo-label bg-product-status-retired text-product-status-retired-text',
      },
      templateStatus: {
        [TemplatePublicationStatus.Private]:
          'h-[20px] rounded-[2px] px-1 flex-inline items-center justify-center typo-label bg-template-status-private text-template-status-private-text',
        [TemplatePublicationStatus.Published]:
          'h-[20px] rounded-[2px] px-1 flex-inline items-center justify-center typo-label bg-template-status-published text-template-status-published-text',
        [TemplatePublicationStatus.Outdated]:
          'h-[20px] rounded-[2px] px-1 flex-inline items-center justify-center typo-label bg-template-status-retired text-template-status-retired-text',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const convertText = (text?: string, uppercase?: boolean) => {
  if (!text) {
    return '';
  }
  const formated = text.replace(/_/g, ' ');
  if (uppercase) {
    return formated.toUpperCase();
  }
  return formated;
};

type PillVariantProps = VariantProps<typeof pillVariants>;

interface PillProps extends React.HTMLAttributes<HTMLDivElement>, PillVariantProps {
  text?: string;
  uppercase?: boolean;
  productStatus?: LifecycleStatus;
}

export const Pill = React.forwardRef<HTMLDivElement, PillProps>(
  ({ className, text, uppercase, variant, size, productStatus, templateStatus, ...props }, ref) => {
    return (
      <div
        className={cn(pillVariants({ variant, productStatus, templateStatus, size }), className)}
        ref={ref}
        {...props}
      >
        {convertText(text, uppercase)}
      </div>
    );
  },
);

Pill.displayName = 'Pill';

type PillContainerProps = {
  maxItems?: number;
  children: React.ReactNode;
  className?: string;
  variant?: PillVariantProps['variant'];
};

export const PillContainer = ({ children, className, maxItems, variant }: PillContainerProps) => {
  const pills = React.Children.toArray(children);
  const visiblePills = maxItems ? pills.slice(0, maxItems) : pills;
  const hiddenPills = maxItems ? pills.slice(maxItems) : [];

  return (
    <div className={cn('flex flex-row gap-x-2', className)}>
      {visiblePills}
      {hiddenPills.length > 0 && (
        <Popover>
          <PopoverTrigger className="h-[20px]">
            {hiddenPills.length > 0 && (
              <Pill size="small" uppercase variant={variant} text={`+${hiddenPills.length}`} />
            )}
          </PopoverTrigger>
          <PopoverContent className="min-w-[200px] max-w-[600px] min-h-[32px] flex flex-row gap-2 justify-start items-center flex-wrap">
            {hiddenPills}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
PillContainer.displayName = 'PillContainer';
